import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { suggestionList } from "@/lib/content";
import { categories, categoryMap } from "@/mocks/categories";

interface SearchOverlayProps {
  open: boolean;
  initialKeyword?: string;
  onClose: () => void;
}

export default function SearchOverlay({ open, initialKeyword = "", onClose }: SearchOverlayProps) {
  const [keyword, setKeyword] = useState(initialKeyword);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setKeyword(initialKeyword);
      const timer = window.setTimeout(() => inputRef.current?.focus(), 80);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [open, initialKeyword]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    if (open) document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const suggestions = useMemo(() => suggestionList(keyword), [keyword]);

  if (!open) return null;

  const submit = (value: string) => {
    const query = value.trim();
    if (!query) return;
    onClose();
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  return (
    <div className="fixed inset-0 z-[60] animate-fade-in">
      <button
        type="button"
        aria-label="关闭搜索"
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-pointer bg-foreground-950/45 backdrop-blur-sm"
      />
      <div className="relative mx-auto mt-0 w-full max-w-3xl px-4 pt-6 md:mt-24 md:px-6 md:pt-0">
        <div className="rounded-lg bg-background-50 p-4 md:p-6">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submit(keyword);
            }}
            className="flex items-center gap-3"
          >
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2.5 focus-within:border-primary-400">
              <i className="ri-search-line text-base text-foreground-500" />
              <input
                ref={inputRef}
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索文章、视频或标签"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
              {keyword ? (
                <button
                  type="button"
                  onClick={() => setKeyword("")}
                  aria-label="清空"
                  className="flex h-5 w-5 cursor-pointer items-center justify-center text-foreground-400 hover:text-foreground-700"
                >
                  <i className="ri-close-line text-base" />
                </button>
              ) : null}
            </div>
            <button
              type="submit"
              className="hidden cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:block dark:text-foreground-950"
            >
              搜索
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="关闭"
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:bg-background-100"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </form>

          <div className="mt-4">
            {keyword ? (
              suggestions.length > 0 ? (
                <ul className="space-y-1">
                  {suggestions.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => {
                          onClose();
                          navigate(`/detail/article/${item.id}`);
                        }}
                        className="flex w-full cursor-pointer items-center justify-between gap-3 rounded-md px-2 py-2.5 text-left hover:bg-background-100"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <i className="ri-file-text-line text-sm text-primary-500" />
                          <span className="truncate text-sm text-foreground-800">{item.title}</span>
                        </span>
                        <span className="whitespace-nowrap text-xs text-foreground-400">
                          {categoryMap[item.category]?.name}
                        </span>
                      </button>
                    </li>
                  ))}
                  <li>
                    <button
                      type="button"
                      onClick={() => submit(keyword)}
                      className="mt-1 flex w-full cursor-pointer items-center gap-2 rounded-md border border-background-200 px-2 py-2.5 text-left text-sm text-primary-600 hover:bg-primary-50"
                    >
                      <i className="ri-arrow-right-line" />
                      查看「{keyword}」的全部结果
                    </button>
                  </li>
                </ul>
              ) : (
                <p className="px-2 py-4 text-sm text-foreground-500">
                  没有找到匹配的内容，试试「文化」「短剧」「厨房」等关键词。
                </p>
              )
            ) : (
              <div>
                <p className="mb-2 text-xs uppercase tracking-[0.18em] text-foreground-400">热门分类</p>
                <div className="flex flex-wrap gap-2">
                  {categories.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        onClose();
                        navigate(`/category/${item.id}`);
                      }}
                      className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}